import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import * as RDialog from '@radix-ui/react-dialog';
import * as RTabs from '@radix-ui/react-tabs';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import * as RSelect from '@radix-ui/react-select';
import * as RSwitch from '@radix-ui/react-switch';
import * as RCheckbox from '@radix-ui/react-checkbox';
import * as RTooltip from '@radix-ui/react-tooltip';
import NumberFlow from '@number-flow/react';
import toast from 'react-hot-toast';
import {
  X, Check, ChevronDown, ChevronUp, ChevronsUpDown, MoreHorizontal, Loader2, Inbox,
  AlertTriangle, Info, CheckCircle2, XCircle, Search,
} from 'lucide-react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, flexRender,
} from '@tanstack/react-table';
import api from '../../../utils/api';
import { money, humanise } from '../../../utils/format';

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// Support Coordination kit (SC Workflow v1 redesign).
//
// The world: a coordinator's case file. Paper-white surfaces, hairline edges, and one identity hue —
// violet, Support Coordination's colour across the app — that marks where you are. Indigo stays the
// action colour so buttons read the same as everywhere else in Rostery. Status colour is semantic
// and only ever means something: emerald paid/done, amber needs a look, red money or compliance at
// risk, blue in motion, violet locked.
//
// Built on Radix primitives (focus, keyboard, ARIA, collision handling) and TanStack Table, styled
// here once so every SC screen reads as one product.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

export { money };
export const words = (s) => humanise(s);
export const errMsg = (e, fallback = 'Something went wrong. Please try again.') =>
  e?.response?.data?.message || e?.message || fallback;
export const hoursOf = (mins) => (Number(mins || 0) / 60).toFixed(2);
export const fmtDate = (d) => {
  if (!d) return '—';
  const s = String(d).slice(0, 10);
  const [y, m, dd] = s.split('-').map(Number);
  if (!y || !m || !dd) return String(d);
  return new Date(Date.UTC(y, m - 1, dd)).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};
export const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }); } catch { return String(d); }
};
export const todayIso = () => {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
};

// ── Data loading ──────────────────────────────────────────────────────────────────────────────
// useLoad('/sc/overview', { from }) → { data, loading, error, reload, setData }. Re-fetches when the
// url or params change; a stale response never overwrites a newer one.
export function useLoad(url, params, { skip = false } = {}) {
  const key = url ? `${url}?${JSON.stringify(params || {})}` : '';
  const [state, setState] = useState({ data: null, loading: !skip && !!url, error: null });
  const seq = useRef(0);
  const reload = useCallback(async () => {
    if (!url || skip) return;
    const my = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.get(url, { params: JSON.parse(key.slice(url.length + 1) || '{}') });
      if (my === seq.current) setState({ data: r.data, loading: false, error: null });
    } catch (e) {
      if (my === seq.current) setState((s) => ({ ...s, loading: false, error: errMsg(e, 'Could not load this. Please try again.') }));
    }
  }, [key, url, skip]);
  useEffect(() => { reload(); }, [reload]);
  const setData = useCallback((fn) => setState((s) => ({ ...s, data: typeof fn === 'function' ? fn(s.data) : fn })), []);
  return { ...state, reload, setData };
}

// Files (NDIA claim files, ABA, PDFs, CSV). The server names the file; JSON errors are unwrapped
// by the api interceptor so the caller's toast shows the real reason.
export async function downloadFile(url, { method = 'get', params, data, filename } = {}) {
  const r = await api.request({ url, method, params, data, responseType: 'blob' });
  const cd = r.headers?.['content-disposition'] || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  const name = filename || (m ? decodeURIComponent(m[1]) : 'download');
  const href = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1500);
  return name;
}

// ── Tones ─────────────────────────────────────────────────────────────────────────────────────
const TONES = {
  violet:  { tile: 'bg-violet-50/70 ring-violet-100',  ink: 'text-violet-700',  dot: 'bg-violet-500',  pill: 'bg-violet-50 text-violet-700 ring-violet-200/60',   icon: 'bg-violet-100 text-violet-700' },
  blue:    { tile: 'bg-sky-50/70 ring-sky-100',        ink: 'text-sky-700',     dot: 'bg-sky-500',     pill: 'bg-sky-50 text-sky-700 ring-sky-200/60',            icon: 'bg-sky-100 text-sky-700' },
  emerald: { tile: 'bg-emerald-50/70 ring-emerald-100', ink: 'text-emerald-700', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200/60', icon: 'bg-emerald-100 text-emerald-700' },
  amber:   { tile: 'bg-amber-50/70 ring-amber-100',    ink: 'text-amber-700',   dot: 'bg-amber-500',   pill: 'bg-amber-50 text-amber-800 ring-amber-200/60',      icon: 'bg-amber-100 text-amber-700' },
  red:     { tile: 'bg-rose-50/70 ring-rose-100',      ink: 'text-rose-700',    dot: 'bg-rose-500',    pill: 'bg-rose-50 text-rose-700 ring-rose-200/60',         icon: 'bg-rose-100 text-rose-700' },
  slate:   { tile: 'bg-slate-50 ring-slate-200/70',    ink: 'text-slate-700',   dot: 'bg-slate-400',   pill: 'bg-slate-100 text-slate-600 ring-slate-200/70',     icon: 'bg-slate-100 text-slate-600' },
  indigo:  { tile: 'bg-indigo-50/70 ring-indigo-100',  ink: 'text-indigo-700',  dot: 'bg-indigo-500',  pill: 'bg-indigo-50 text-indigo-700 ring-indigo-200/60',   icon: 'bg-indigo-100 text-indigo-700' },
};
export const tone = (t) => TONES[t] || TONES.slate;

// One status vocabulary for every workflow (referrals, hours, claim lines, batches, invoices, bills).
const STATUS = {
  // generic
  draft: 'slate', new: 'amber', open: 'amber', closed: 'slate', cancelled: 'slate', void: 'slate', archived: 'slate',
  // referrals
  in_review: 'blue', information_requested: 'amber', accepted: 'emerald', declined: 'red', waitlisted: 'blue',
  sent: 'blue', acknowledged: 'blue', withdrawn: 'slate', expired: 'amber', submitted: 'violet', completed: 'emerald',
  // hours / pay
  returned: 'red', approved: 'emerald', billed: 'violet', exported: 'violet', paid: 'emerald', legacy: 'slate', unlocked: 'amber',
  // claims
  ready: 'emerald', blocked: 'red', generated: 'blue', uploaded: 'blue', results_imported: 'violet', in_claim: 'blue',
  capped: 'amber', rejected: 'red', partially_paid: 'amber', written_off: 'slate', reclaimed: 'blue', escalated: 'red',
  // invoices
  issued: 'blue', overdue: 'red', part_paid: 'amber', reissued: 'slate',
  // engagement
  active: 'emerald', on_hold: 'amber', inactive: 'slate',
};
export function Pill({ status, label, tone: t, className, size = 'sm' }) {
  const key = String(status || '').toLowerCase();
  const c = tone(t || STATUS[key] || 'slate');
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap ring-1 ring-inset',
      size === 'xs' ? 'text-[11.5px] px-1.5 py-px' : 'text-[12px] px-2 py-0.5', c.pill, className)}>
      <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', c.dot)} aria-hidden />
      {label || humanise(status) || '—'}
    </span>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────────────────────
// The screen's title carries itself: no kicker above it (the top bar already says Support
// Coordination). `before` takes a breadcrumb when the screen sits inside another.
export function PageHeader({ title, sub, actions, children, before }) {
  return (
    <header className="mb-6">
      {before}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-[26px] leading-tight font-semibold tracking-[-0.02em] text-slate-900">{title}</h1>
          {sub && <p className="mt-1 text-sm text-slate-500 max-w-2xl">{sub}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

export function Panel({ title, sub, actions, children, className, pad = true, icon: Icon, hue }) {
  return (
    <section className={clsx('bg-white rounded-2xl border border-slate-200/80 shadow-card', className)}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3">
          <div className="flex items-start gap-3 min-w-0">
            {Icon && <span className={clsx('mt-0.5 w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0', tone(hue || 'violet').icon)}><Icon size={16} aria-hidden /></span>}
            <div className="min-w-0">
              {title && <h2 className="text-[15px] font-semibold text-slate-900 leading-6">{title}</h2>}
              {sub && <p className="text-[12.5px] text-slate-500 mt-0.5">{sub}</p>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
        </div>
      )}
      <div className={clsx(pad && 'px-5 pb-5', !title && !actions && pad && 'pt-5')}>{children}</div>
    </section>
  );
}

// A count that needs someone: the number leads, the label is demoted, the tint carries meaning.
export function StatTile({ label, value, sub, tone: t = 'slate', icon: Icon, onClick, isMoney, suffix, active }) {
  const c = tone(t);
  const Tag = onClick ? 'button' : 'div';
  const n = Number(value) || 0;
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick}
      className={clsx('group text-left rounded-2xl ring-1 ring-inset px-4 py-3.5 transition-all duration-150 w-full', c.tile,
        onClick && 'hover:-translate-y-px hover:shadow-card-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        active && 'ring-2 ring-indigo-500')}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-slate-700">{label}</span>
        {Icon && <Icon size={15} className={clsx(c.ink, 'opacity-80')} aria-hidden />}
      </div>
      <div className={clsx('mt-1.5 text-[26px] leading-none font-semibold tabular-nums tracking-[-0.02em]', n ? c.ink : 'text-slate-500')}>
        <NumberFlow value={n} locales="en-AU" format={isMoney ? { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 } : { maximumFractionDigits: 2 }} />
        {suffix && <span className="ml-1 text-[13px] font-medium text-slate-500">{suffix}</span>}
      </div>
      {sub && <div className="mt-1.5 text-[12px] text-slate-600 truncate">{sub}</div>}
    </Tag>
  );
}

export function Money({ value, className, animate = false }) {
  const n = Number(value) || 0;
  if (animate) return <NumberFlow className={clsx('tabular-nums', className)} value={n} locales="en-AU" format={{ style: 'currency', currency: 'AUD' }} />;
  return <span className={clsx('tabular-nums', n < 0 && 'text-rose-600', className)}>{money(n)}</span>;
}

export function Banner({ tone: t = 'blue', title, children, action, icon }) {
  const map = { blue: [Info, 'bg-sky-50 border-sky-200 text-sky-900'], amber: [AlertTriangle, 'bg-amber-50 border-amber-200 text-amber-900'],
    red: [XCircle, 'bg-rose-50 border-rose-200 text-rose-900'], emerald: [CheckCircle2, 'bg-emerald-50 border-emerald-200 text-emerald-900'],
    violet: [Info, 'bg-violet-50 border-violet-200 text-violet-900'] };
  const [I, cls] = map[t] || map.blue;
  const Ic = icon || I;
  return (
    <div role={t === 'red' ? 'alert' : 'status'} className={clsx('flex items-start gap-3 rounded-xl border px-4 py-3 text-sm', cls)}>
      <Ic size={17} className="mt-0.5 flex-shrink-0 opacity-80" aria-hidden />
      <div className="flex-1 min-w-0">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={clsx(title && 'mt-0.5', 'opacity-90')}>{children}</div>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export function Empty({ icon: Icon = Inbox, title = 'Nothing here yet', children, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-6">
      <span className="w-11 h-11 rounded-2xl bg-violet-50 text-violet-600 flex items-center justify-center"><Icon size={20} aria-hidden /></span>
      <p className="mt-3 text-sm font-semibold text-slate-800">{title}</p>
      {children && <p className="mt-1 text-[13px] text-slate-500 max-w-sm">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }) {
  return <div className={clsx('animate-pulse rounded-lg bg-slate-100', className)} aria-hidden />;
}
export function LoadingBlock({ rows = 4 }) {
  return <div className="space-y-2.5 py-2" aria-busy="true" aria-label="Loading">{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} className="h-9" />)}</div>;
}
export function ErrorBlock({ error, onRetry }) {
  return <Banner tone="red" title="Couldn't load" action={onRetry && <Button size="sm" variant="secondary" onClick={onRetry}>Try again</Button>}>{error}</Banner>;
}

// ── Buttons ───────────────────────────────────────────────────────────────────────────────────
const BTN = {
  primary:   'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm',
  secondary: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 hover:ring-slate-300',
  ghost:     'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  danger:    'bg-rose-600 text-white hover:bg-rose-700 shadow-sm',
  soft:      'bg-violet-50 text-violet-700 hover:bg-violet-100',
  success:   'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm',
};
export const Button = React.forwardRef(function Button({ variant = 'primary', size = 'md', loading, icon: Icon, children, className, disabled, type = 'button', ...rest }, ref) {
  return (
    <button ref={ref} type={type} disabled={disabled || loading}
      className={clsx('inline-flex items-center justify-center gap-1.5 font-semibold rounded-xl transition-colors duration-150 whitespace-nowrap',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed',
        size === 'sm' ? 'text-[12.5px] h-10 sm:h-8 px-3' : size === 'lg' ? 'text-sm h-11 px-5' : 'text-[13px] h-11 sm:h-9 px-3.5',
        BTN[variant], className)} {...rest}>
      {loading ? <Loader2 size={14} className="animate-spin" aria-hidden /> : Icon ? <Icon size={size === 'sm' ? 13 : 15} aria-hidden /> : null}
      {children}
    </button>
  );
});

// ── Tabs (Radix) ──────────────────────────────────────────────────────────────────────────────
export function Tabs({ value, onChange, tabs, children, className, variant = 'underline', label = 'Section' }) {
  const many = tabs.length > 3;
  return (
    <RTabs.Root value={value} onValueChange={onChange} className={className}>
      {many && (
        <div className="sm:hidden mb-4">
          <label className="block text-[12.5px] font-semibold text-slate-700 mb-1.5" htmlFor={`sc-tabs-${label}`}>{label}</label>
          <Select id={`sc-tabs-${label}`} value={value} onChange={onChange}
            options={tabs.map((t) => ({ value: t.value, label: t.count > 0 ? `${t.label} (${t.count > 99 ? '99+' : t.count})` : t.label }))} />
        </div>
      )}
      <RTabs.List aria-label={label}
        className={clsx(many && 'max-sm:hidden', 'flex gap-1 overflow-x-auto tab-scroll [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', variant === 'underline'
          ? 'border-b border-slate-200 mb-5 [mask-image:linear-gradient(to_right,#000_calc(100%-24px),transparent)] pr-6'
          : 'bg-slate-100/80 p-1 rounded-xl max-w-full w-fit mb-4')}>
        {tabs.map((t) => (
          <RTabs.Trigger key={t.value} value={t.value}
            className={clsx('relative inline-flex items-center gap-2 text-[13px] font-semibold whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-lg',
              variant === 'underline'
                ? 'px-3 pb-2.5 pt-1.5 text-slate-500 hover:text-slate-800 data-[state=active]:text-violet-700 after:absolute after:left-2 after:right-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-transparent data-[state=active]:after:bg-violet-600'
                : 'px-3 py-1.5 text-slate-600 hover:text-slate-900 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm')}>
            {t.icon && <t.icon size={14} aria-hidden />}
            {t.label}
            {t.count > 0 && <span className={clsx('text-[11px] tabular-nums font-bold px-1.5 py-px rounded-full', t.alert ? 'bg-rose-600 text-white' : 'bg-slate-200/80 text-slate-700')}>{t.count > 99 ? '99+' : t.count}</span>}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {children}
    </RTabs.Root>
  );
}
export const TabPanel = ({ value, children }) => <RTabs.Content value={value} className="outline-none">{children}</RTabs.Content>;

// Segmented filter (not navigation): a small set of mutually exclusive views of one list.
export function Segmented({ value, onChange, options, size = 'md' }) {
  return (
    <div role="radiogroup" className="inline-flex bg-slate-100/80 p-0.5 rounded-xl">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)}
          className={clsx('rounded-[10px] font-semibold transition-colors whitespace-nowrap inline-flex items-center gap-1.5',
            size === 'sm' ? 'text-[12.5px] px-3 py-2.5 sm:px-2.5 sm:py-1' : 'text-[13px] px-3 py-2.5 sm:py-1.5',
            value === o.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')}>
          {o.label}
          {o.count > 0 && <span className={clsx('tabular-nums text-[11.5px] font-bold', o.alert ? 'text-rose-700' : 'text-slate-500')}>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

// ── Overlays (Radix Dialog) ───────────────────────────────────────────────────────────────────
// Sheet: the side panel every record opens in. Modal: short, centred decisions.
export function Sheet({ open, onClose, title, sub, icon: Icon, children, footer, width = 'md', hue = 'violet' }) {
  const w = width === 'lg' ? 'sm:max-w-3xl' : width === 'xl' ? 'sm:max-w-5xl' : 'sm:max-w-xl';
  return (
    <RDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-[60] bg-slate-900/30 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <RDialog.Content aria-describedby={undefined}
          className={clsx('fixed z-[61] inset-y-0 right-0 w-full bg-white shadow-modal flex flex-col outline-none data-[state=open]:animate-drawer-in', w)}>
          <div className="flex items-start gap-3 px-6 pt-5 pb-4 border-b border-slate-100">
            {Icon && <span className={clsx('w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0', tone(hue).icon)}><Icon size={17} aria-hidden /></span>}
            <div className="flex-1 min-w-0">
              <RDialog.Title className="text-[17px] font-semibold text-slate-900 leading-6">{title}</RDialog.Title>
              {sub && <p className="text-[12.5px] text-slate-500 mt-0.5">{sub}</p>}
            </div>
            <RDialog.Close className="p-1.5 -mr-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" aria-label="Close">
              <X size={18} aria-hidden />
            </RDialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="px-6 py-3.5 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export function Modal({ open, onClose, title, sub, children, footer, width = 'md' }) {
  const w = width === 'sm' ? 'max-w-md' : width === 'lg' ? 'max-w-2xl' : 'max-w-lg';
  return (
    <RDialog.Root open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-[70] bg-slate-900/35 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <RDialog.Content aria-describedby={undefined}
          className={clsx('fixed z-[71] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] bg-white rounded-2xl shadow-modal outline-none data-[state=open]:animate-scale-in max-h-[90vh] flex flex-col', w)}>
          <div className="px-6 pt-5 pb-3">
            <RDialog.Title className="text-[17px] font-semibold text-slate-900">{title}</RDialog.Title>
            {sub && <p className="text-[13px] text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className="px-6 pb-5 overflow-y-auto">{children}</div>
          {footer && <div className="px-6 py-3.5 border-t border-slate-100 flex items-center justify-end gap-2">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

// A decision that needs a reason on record (return hours, write off, unlock, decline).
export function ReasonModal({ open, onClose, title, sub, label = 'Reason', confirmLabel = 'Confirm', danger, onConfirm, extra }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const go = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={title} sub={sub} width="sm"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant={danger ? 'danger' : 'primary'} loading={busy} disabled={!reason.trim()} onClick={go}>{confirmLabel}</Button></>}>
      {extra}
      <Field label={label} required>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
      </Field>
    </Modal>
  );
}

// A yes/no decision with consequences (remove, discard, generate). Use instead of window.confirm.
export function ConfirmModal({ open, onClose, title, children, confirmLabel = 'Confirm', danger, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onConfirm(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={title} width="sm"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={go}>{confirmLabel}</Button></>}>
      {children && <div className="text-[13.5px] text-slate-600 leading-relaxed">{children}</div>}
    </Modal>
  );
}

// ── Row menu (Radix DropdownMenu) ─────────────────────────────────────────────────────────────
export function RowMenu({ items, label = 'More actions', align = 'end' }) {
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <RMenu.Root>
      <RMenu.Trigger asChild>
        <button type="button" aria-label={label} onClick={(e) => e.stopPropagation()}
          className="p-3 sm:p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 data-[state=open]:bg-slate-100 data-[state=open]:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
          <MoreHorizontal size={16} aria-hidden />
        </button>
      </RMenu.Trigger>
      <RMenu.Portal>
        <RMenu.Content align={align} sideOffset={4} onClick={(e) => e.stopPropagation()}
          className="z-[80] min-w-[200px] bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80 p-1 data-[state=open]:animate-scale-in">
          {list.map((it, i) => it === 'sep' ? <RMenu.Separator key={i} className="my-1 h-px bg-slate-100" /> : (
            <RMenu.Item key={i} disabled={it.disabled} onSelect={() => it.onClick?.()}
              className={clsx('flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] cursor-pointer outline-none select-none',
                'data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed',
                it.danger ? 'text-rose-600 data-[highlighted]:bg-rose-50' : 'text-slate-700 data-[highlighted]:bg-slate-100')}>
              {it.icon && <it.icon size={14} aria-hidden className="opacity-70" />}
              <span className="flex-1">{it.label}</span>
            </RMenu.Item>
          ))}
        </RMenu.Content>
      </RMenu.Portal>
    </RMenu.Root>
  );
}

// ── Form controls ─────────────────────────────────────────────────────────────────────────────
let fieldSeq = 0;
export function Field({ label, hint, error, required, children, className }) {
  const [id] = useState(() => `scf-${++fieldSeq}`);
  const child = React.isValidElement(children) && !children.props.id ? React.cloneElement(children, { id, 'aria-invalid': error ? true : undefined }) : children;
  return (
    <div className={clsx('space-y-1.5', className)}>
      {label && <label htmlFor={id} className="block text-[12.5px] font-semibold text-slate-700">{label}{required && <span className="text-rose-500 ml-0.5" aria-hidden>*</span>}</label>}
      {child}
      {error ? <p className="text-[12px] text-rose-600">{error}</p> : hint ? <p className="text-[12px] text-slate-500">{hint}</p> : null}
    </div>
  );
}
const CONTROL = 'w-full rounded-xl border-0 bg-slate-50 ring-1 ring-inset ring-slate-200 px-3 text-[13.5px] text-slate-900 placeholder:text-slate-500 transition-shadow focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white disabled:opacity-60 aria-[invalid=true]:ring-rose-400';
export const Input = React.forwardRef(function Input({ className, ...p }, ref) { return <input ref={ref} className={clsx(CONTROL, 'h-11 sm:h-10', className)} {...p} />; });
export const Textarea = React.forwardRef(function Textarea({ className, ...p }, ref) { return <textarea ref={ref} className={clsx(CONTROL, 'py-2.5 leading-relaxed', className)} {...p} />; });
export function SearchInput({ value, onChange, placeholder = 'Search', className }) {
  return (
    <div className={clsx('relative', className)}>
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
        className={clsx(CONTROL, 'h-9 pl-9 bg-white')} />
    </div>
  );
}

// Select on Radix (keyboard, typeahead, collision-aware). options: [{ value, label, hint }]. Empty
// string isn't a legal Radix value, so '' maps to an internal sentinel.
const NONE = '__none__';
export function Select({ value, onChange, options, placeholder = 'Choose…', id, disabled, className, allowEmpty, emptyLabel = 'None' }) {
  const v = value === '' || value == null ? (allowEmpty ? NONE : undefined) : String(value);
  return (
    <RSelect.Root value={v} onValueChange={(x) => onChange(x === NONE ? '' : x)} disabled={disabled}>
      <RSelect.Trigger id={id} className={clsx(CONTROL, 'h-11 sm:h-10 flex items-center justify-between gap-2 text-left data-[placeholder]:text-slate-500', className)}>
        <RSelect.Value placeholder={placeholder} />
        <RSelect.Icon><ChevronDown size={15} className="text-slate-400" aria-hidden /></RSelect.Icon>
      </RSelect.Trigger>
      <RSelect.Portal>
        <RSelect.Content position="popper" sideOffset={4}
          className="z-[90] max-h-[min(var(--radix-select-content-available-height),320px)] min-w-[var(--radix-select-trigger-width)] overflow-hidden bg-white rounded-xl shadow-card-deep ring-1 ring-slate-200/80">
          <RSelect.Viewport className="p-1">
            {allowEmpty && <SelectItem value={NONE} label={emptyLabel} />}
            {options.map((o) => <SelectItem key={o.value} value={String(o.value)} label={o.label} hint={o.hint} />)}
          </RSelect.Viewport>
        </RSelect.Content>
      </RSelect.Portal>
    </RSelect.Root>
  );
}
function SelectItem({ value, label, hint }) {
  return (
    <RSelect.Item value={value} className="relative flex items-start gap-2 pl-7 pr-3 py-2 rounded-lg text-[13px] text-slate-700 cursor-pointer outline-none select-none data-[highlighted]:bg-indigo-50 data-[highlighted]:text-indigo-900">
      <RSelect.ItemIndicator className="absolute left-2 top-2.5"><Check size={13} aria-hidden /></RSelect.ItemIndicator>
      <div className="min-w-0">
        <RSelect.ItemText>{label}</RSelect.ItemText>
        {hint && <p className="text-[12px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
    </RSelect.Item>
  );
}

export function Switch({ checked, onChange, label, hint, disabled }) {
  const [id] = useState(() => `scs-${++fieldSeq}`);
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-[13px] font-semibold text-slate-800">{label}</label>
        {hint && <p className="text-[12px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
      <RSwitch.Root id={id} checked={!!checked} onCheckedChange={onChange} disabled={disabled}
        className="relative w-10 h-6 flex-shrink-0 rounded-full bg-slate-200 data-[state=checked]:bg-indigo-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:opacity-50">
        <RSwitch.Thumb className="block w-5 h-5 bg-white rounded-full shadow translate-x-0.5 transition-transform data-[state=checked]:translate-x-[18px]" />
      </RSwitch.Root>
    </div>
  );
}

export function Checkbox({ checked, onChange, label, disabled, 'aria-label': ariaLabel }) {
  const box = (
    <RCheckbox.Root checked={checked === 'indeterminate' ? 'indeterminate' : !!checked} onCheckedChange={(v) => onChange(v === true)} disabled={disabled}
      aria-label={label ? undefined : ariaLabel} onClick={(e) => e.stopPropagation()}
      className="w-[18px] h-[18px] flex-shrink-0 rounded-[5px] ring-1 ring-inset ring-slate-300 bg-white data-[state=checked]:bg-indigo-600 data-[state=checked]:ring-indigo-600 data-[state=indeterminate]:bg-indigo-600 data-[state=indeterminate]:ring-indigo-600 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50">
      <RCheckbox.Indicator className="text-white">
        {checked === 'indeterminate' ? <span className="block w-2 h-0.5 bg-white rounded" /> : <Check size={12} strokeWidth={3} aria-hidden />}
      </RCheckbox.Indicator>
    </RCheckbox.Root>
  );
  if (!label) return box;
  return <label className="inline-flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer select-none">{box}{label}</label>;
}

export function Tip({ content, children, side = 'top' }) {
  if (!content) return children;
  return (
    <RTooltip.Provider delayDuration={250}>
      <RTooltip.Root>
        <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
        <RTooltip.Portal>
          <RTooltip.Content side={side} sideOffset={6} className="z-[95] max-w-xs rounded-lg bg-slate-900 text-white text-[12px] leading-snug px-2.5 py-1.5 shadow-lg data-[state=delayed-open]:animate-fade-in">
            {content}
            <RTooltip.Arrow className="fill-slate-900" />
          </RTooltip.Content>
        </RTooltip.Portal>
      </RTooltip.Root>
    </RTooltip.Provider>
  );
}

// ── DataTable (TanStack) ──
// Server-paged lists sort on the server: pass sorting ([{ id, desc }]) and onSortingChange, and the
// table only reports clicks (manual sorting); column ids are then the server's sort keys.
// mobileCard(row): on phones each row renders as this card instead of a table row.────────────────────────────────────────────────────────────────────
// columns: TanStack column defs. Extra meta: { align: 'right', width, className }.
// selectable: checkbox column; onSelectionChange(rows). Footer: a totals row (node).
export function DataTable({
  columns, data, loading, error, onRetry, empty, onRowClick, selectable, onSelectionChange, getRowId,
  footer, initialSort, rowClassName, dense, maxHeight, isRowSelectable, sorting: sortingProp, onSortingChange, mobileCard,
}) {
  const [sortingState, setSortingState] = useState(initialSort || []);
  const manual = !!onSortingChange;
  const sorting = manual ? (sortingProp || []) : sortingState;
  const setSorting = manual ? (u) => onSortingChange(typeof u === 'function' ? u(sorting) : u) : setSortingState;
  const [rowSelection, setRowSelection] = useState({});
  const rows = useMemo(() => data || [], [data]);
  const cols = useMemo(() => {
    if (!selectable) return columns;
    return [{
      id: '_select', enableSorting: false, meta: { width: 40 },
      header: ({ table }) => (
        <Checkbox aria-label="Select all"
          checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false}
          onChange={(v) => table.toggleAllRowsSelected(v)} />
      ),
      cell: ({ row }) => <Checkbox aria-label="Select row" checked={row.getIsSelected()} disabled={!row.getCanSelect()} onChange={(v) => row.toggleSelected(v)} />,
    }, ...columns];
  }, [columns, selectable]);
  const table = useReactTable({
    data: rows, columns: cols, state: { sorting, rowSelection },
    onSortingChange: setSorting, onRowSelectionChange: setRowSelection,
    enableRowSelection: selectable ? (isRowSelectable || true) : false,
    getRowId: getRowId || ((r, i) => String(r.id ?? i)),
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), manualSorting: manual,
  });
  const selected = table.getSelectedRowModel().rows;
  const selKey = selected.map((r) => r.id).join(',');
  const cb = useRef(onSelectionChange);
  const selRef = useRef([]);
  cb.current = onSelectionChange;
  selRef.current = selected.map((r) => r.original);
  useEffect(() => { if (cb.current) cb.current(selRef.current); }, [selKey]);
  useEffect(() => { setRowSelection({}); }, [data]);

  if (error) return <div className="py-2"><ErrorBlock error={error} onRetry={onRetry} /></div>;
  if (loading && !rows.length) return <LoadingBlock rows={5} />;
  if (!rows.length) return empty || <Empty />;
  const py = dense ? 'py-2' : 'py-3';
  const cards = mobileCard ? (
    <ul className="sm:hidden divide-y divide-slate-100 -mx-1">
      {table.getRowModel().rows.map((r) => (
        <li key={r.id} className={clsx('px-1 py-3', r.getIsSelected() && 'bg-indigo-50/60')}>
          <div className="flex items-start gap-3">
            {selectable && r.getCanSelect() && <div className="pt-0.5"><Checkbox aria-label="Select row" checked={r.getIsSelected()} onChange={(v) => r.toggleSelected(v)} /></div>}
            <div className={clsx('flex-1 min-w-0', onRowClick && 'cursor-pointer')} onClick={onRowClick ? () => onRowClick(r.original) : undefined}>{mobileCard(r.original)}</div>
          </div>
        </li>
      ))}
    </ul>
  ) : null;
  return (
    <>
    {cards}
    <div className={clsx('overflow-auto -mx-px', maxHeight && 'overscroll-contain', mobileCard && 'hidden sm:block')} style={maxHeight ? { maxHeight } : undefined}>
      <table className="w-full text-[13px] border-separate border-spacing-0">
        <thead className="sticky top-0 z-[1]">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => {
                const meta = h.column.columnDef.meta || {};
                const can = h.column.getCanSort();
                const dir = h.column.getIsSorted();
                return (
                  <th key={h.id} scope="col" style={meta.width ? { width: meta.width } : undefined}
                    aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
                    className={clsx('bg-slate-50/95 backdrop-blur text-[12px] font-semibold text-slate-600 border-y border-slate-200/80 px-3 py-2 first:pl-4 last:pr-4 first:rounded-l-lg last:rounded-r-lg',
                      meta.align === 'right' ? 'text-right' : 'text-left')}>
                    {h.isPlaceholder ? null : can ? (
                      <button type="button" onClick={h.column.getToggleSortingHandler()}
                        className={clsx('inline-flex items-center gap-1 hover:text-slate-800', meta.align === 'right' && 'flex-row-reverse')}>
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {dir === 'asc' ? <ChevronUp size={12} aria-hidden /> : dir === 'desc' ? <ChevronDown size={12} aria-hidden /> : <ChevronsUpDown size={12} className="opacity-40" aria-hidden />}
                      </button>
                    ) : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((r) => (
            <tr key={r.id} onClick={onRowClick ? () => onRowClick(r.original) : undefined}
              className={clsx('group transition-colors', onRowClick && 'cursor-pointer hover:bg-indigo-50/40', r.getIsSelected() && 'bg-indigo-50/60', rowClassName?.(r.original))}>
              {r.getVisibleCells().map((c) => {
                const meta = c.column.columnDef.meta || {};
                return (
                  <td key={c.id} className={clsx('px-3 first:pl-4 last:pr-4 border-b border-slate-100 text-slate-700 align-middle', py,
                    meta.align === 'right' && 'text-right tabular-nums', meta.className)}>
                    {flexRender(c.column.columnDef.cell, c.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot><tr className="bg-slate-50/70 font-semibold text-slate-800">{footer}</tr></tfoot>}
      </table>
      {loading && <div className="flex items-center gap-2 text-[12px] text-slate-500 px-4 py-2"><Loader2 size={13} className="animate-spin" aria-hidden /> Refreshing…</div>}
    </div>
    </>
  );
}

// Toolbar above a table: filters left, actions right.
export function Toolbar({ children, actions }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

// Bulk action bar that slides up when rows are selected.
export function SelectionBar({ count, children, onClear }) {
  if (!count) return null;
  return (
    <div className="sticky bottom-4 z-20 mx-auto w-fit flex items-center gap-3 bg-slate-900 text-white rounded-2xl pl-4 pr-2 py-2 shadow-modal animate-fade-up">
      <span className="text-[13px] font-semibold tabular-nums">{count} selected</span>
      <span className="w-px h-5 bg-white/15" aria-hidden />
      <div className="flex items-center gap-1.5">{children}</div>
      <button type="button" onClick={onClear} className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10" aria-label="Clear selection"><X size={15} aria-hidden /></button>
    </div>
  );
}

// Definition list for record detail panels.
export function Facts({ items, cols = 2 }) {
  return (
    <dl className={clsx('grid gap-x-6 gap-y-3', cols === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2')}>
      {items.filter(Boolean).map(([k, v], i) => (
        <div key={i} className="min-w-0">
          <dt className="text-[12px] font-medium text-slate-500">{k}</dt>
          <dd className="mt-0.5 text-[13.5px] text-slate-900 break-words">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

// Steps for multi-stage flows (claim batch: preview → generate → upload → results).
export function Steps({ steps, current }) {
  return (
    <ol className="flex items-center gap-2 overflow-x-auto">
      {steps.map((s, i) => {
        const done = i < current; const on = i === current;
        return (
          <li key={s} className="flex items-center gap-2 flex-shrink-0">
            <span className={clsx('w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center',
              done ? 'bg-emerald-500 text-white' : on ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-500')}>
              {done ? <Check size={12} strokeWidth={3} aria-hidden /> : i + 1}
            </span>
            <span className={clsx('text-[12.5px] font-semibold', on ? 'text-slate-900' : 'text-slate-500')}>{s}</span>
            {i < steps.length - 1 && <span className="w-6 h-px bg-slate-200" aria-hidden />}
          </li>
        );
      })}
    </ol>
  );
}

// A thin progress meter (budget used, hours against target).
export function Meter({ value, max, tone: t = 'violet', label }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const bar = pct >= 100 ? 'bg-rose-500' : pct >= 90 ? 'bg-amber-500' : tone(t).dot;
  return (
    <div role="meter" aria-valuemin={0} aria-valuemax={max || 0} aria-valuenow={value || 0} aria-label={label}
      className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
      <div className={clsx('h-full w-full rounded-full origin-left transition-transform duration-500 ease-out', bar)} style={{ transform: `scaleX(${pct / 100})` }} />
    </div>
  );
}

export function Avatar({ first, last, size = 28 }) {
  const t = `${(first || '?')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
  return <span className="inline-flex items-center justify-center rounded-full bg-violet-100 text-violet-700 font-semibold flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.38 }} aria-hidden>{t}</span>;
}
